<template>
  <EditableList
    :items="modelValue"
    :single="single"
    add-label="Add ability"
    remove-label="Remove ability"
    @add="addAbility"
    @remove="removeAbility"
  >
    <template #row="{ item: ability, index }">
      <div class="ability-fields">
        <StIdField
          class="ability-fields__id"
          :model-value="ability.id"
          prefix="ability"
          @update:model-value="updateField(index, 'id', $event)"
        />
        <!-- Row 1: name | timing -->
        <StField class="field--half" label="Name">
          <StTextInput
            :model-value="ability.name"
            placeholder="Ability name"
            @update:model-value="updateField(index, 'name', $event)"
          />
        </StField>
        <StField class="field--half" label="Timing">
          <!-- Passive abilities have no timing — it always reads "Passive".
               Non-passive timing offers suggestions from previously used timings. -->
          <StSuggestInput
            :model-value="isPassive(ability) ? 'Passive' : ability.timing"
            :disabled="isPassive(ability)"
            placeholder="e.g. Hero Phase"
            :suggestions="suggestions"
            @update:model-value="updateField(index, 'timing', $event)"
            @select="selectTiming(index, $event)"
            @forget="forget"
            @blur="record(ability.timing)"
          />
        </StField>
        <!-- Row 2: type, color, icon, cost. The type is fixed (set + disabled)
             when forced by the lore kind — spell/manifestation are always Spell,
             prayer is always Prayer. -->
        <StField label="Type">
          <StSelect
            :model-value="ability.type"
            :disabled="forcedType !== undefined"
            @update:model-value="updateType(index, Number($event))"
          >
            <option v-for="opt in abilityTypeOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </StSelect>
        </StField>
        <StField label="Color">
          <StSelect
            :model-value="ability.color"
            @update:model-value="updateField(index, 'color', $event)"
          >
            <option v-for="c in colorOptions" :key="c" :value="c">{{ c }}</option>
          </StSelect>
        </StField>
        <StField label="Icon">
          <StSelect
            :model-value="ability.typeAttribute"
            @update:model-value="updateField(index, 'typeAttribute', $event)"
          >
            <option v-for="i in iconOptions" :key="i" :value="i">{{ i }}</option>
          </StSelect>
        </StField>
        <StField label="Cost">
          <StTextInput
            :model-value="ability.cost ?? ''"
            placeholder="e.g. 1 CP"
            @update:model-value="updateField(index, 'cost', $event || null)"
          />
        </StField>
        <!-- Declare (non-passive) and Effect are full-width textareas of equal height. -->
        <StField v-if="!isPassive(ability)" class="field--full" label="Declare">
          <StTextarea
            :model-value="ability.declare ?? ''"
            placeholder="What the player declares…"
            @update:model-value="updateField(index, 'declare', $event || null)"
          />
        </StField>
        <StField class="field--full" label="Effect">
          <StTextarea
            :model-value="ability.effect"
            placeholder="Describe the ability effect..."
            @update:model-value="updateField(index, 'effect', $event)"
          />
        </StField>
        <!-- Keywords last. A forced keyword (lores) is shown as a locked chip and
             managed automatically, like the unit's derived keywords. -->
        <StField group class="field--full" label="Keywords">
          <ul v-if="forcedKeyword" class="locked-kw">
            <li class="locked-kw__chip" title="Set by the lore type">
              <FontAwesomeIcon icon="lock" class="locked-kw__lock" />
              {{ forcedKeywordLabel }}
            </li>
          </ul>
          <KeywordsInput
            :model-value="displayKeywords(ability)"
            placeholder="e.g. CORE, ONCE PER TURN"
            @update:model-value="updateField(index, 'keywords', $event)"
          />
        </StField>
      </div>
    </template>
  </EditableList>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { AbilityType, makeId, type IAbility } from '@scrolldex/shared';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  StField,
  StTextInput,
  StSuggestInput,
  StTextarea,
  StSelect,
  StIdField,
} from './ui/index.js';
import EditableList from './EditableList.vue';
import KeywordsInput from './KeywordsInput.vue';
import { useWorkspace } from '../state/workspace.js';
import { useTimingSuggestions } from '../composables/useTimingSuggestions.js';

const props = defineProps<{
  modelValue: IAbility[];
  // Single-ability mode (e.g. battle formations): exactly one ability, no
  // add/remove chrome. Seeds one if the list is empty.
  single?: boolean;
  // When set (lores), every ability is forced to this type — the type selector
  // is shown set + disabled, and the type is normalized on every change.
  forcedType?: number;
  // When set (lores), this keyword is always present — shown as a locked chip
  // (kept out of the editable chips) and re-injected on every change
  // (e.g. "**^^Spell^^**").
  forcedKeyword?: string;
}>();

// The forced keyword stripped of its `**^^…^^**` formatting, for the locked chip.
const forcedKeywordLabel = computed(() => (props.forcedKeyword ?? '').replace(/[*^]/g, '').trim());

const ws = useWorkspace();
const { suggestions, record, recordMany, forget } = useTimingSuggestions();

// Picking a suggestion sets the timing and (re-)remembers it.
function selectTiming(index: number, timing: string) {
  updateField(index, 'timing', timing);
  record(timing);
}

// Seed suggestions from every authored ability in the loaded bundle plus this
// editor's current abilities, so loaded timings are suggestible immediately.
onMounted(() => {
  const seen: (string | null | undefined)[] = [];
  for (const plan of Object.values(ws.authored.value)) {
    for (const ability of plan.abilities ?? []) seen.push(ability.timing);
  }
  for (const ability of props.modelValue) seen.push(ability.timing);
  recordMany(seen);
  // Single-ability sections always show exactly one ability — seed it so the
  // (chrome-less) editor isn't blank for a freshly-created or legacy entry.
  if (props.single && props.modelValue.length === 0) addAbility();
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: IAbility[]): void;
}>();

// Build label/value pairs from the numeric enum.
// Object.entries on a numeric enum yields entries like ['Activated', 0], ['Passive', 1], etc.
// plus reverse-lookup entries like [0, 'Activated'] — filter to only string keys.
const abilityTypeOptions = (Object.entries(AbilityType) as [string, string | number][])
  .filter(([key]) => isNaN(Number(key)))
  .map(([label, value]) => ({ label, value: value as number }));

// The phase-derived ability colors (see convertAosPhaseToColor in shared); these
// match the bg-<color> classes the AbilityCard renderer understands.
const colorOptions = ['Black', 'Yellow', 'Gray', 'Blue', 'Orange', 'Red', 'Purple', 'Green'];

// Header icon (stored on typeAttribute); values match the AbilityCard icon map.
const iconOptions = [
  'Offensive',
  'Defensive',
  'Shooting',
  'Movement',
  'Rallying',
  'Control',
  'Special',
  'Damage',
];

function newAbility(): IAbility {
  return {
    id: makeId('ability'),
    name: '',
    type: props.forcedType ?? AbilityType.Activated,
    superType: 'Ability',
    timing: '',
    keywords: props.forcedKeyword ? [props.forcedKeyword] : [],
    effect: '',
    color: 'Black',
    typeAttribute: 'Special',
    declare: null,
    usedBy: null,
    cost: null,
    unlockCondition: null,
  };
}

// Apply the forced type/keyword (lores) before emitting, so the data always
// matches the lore kind regardless of how it was edited.
function normalize(list: IAbility[]): IAbility[] {
  if (props.forcedType === undefined) return list;
  const kw = props.forcedKeyword;
  return list.map((a) => ({
    ...a,
    type: props.forcedType!,
    keywords: kw && !a.keywords.includes(kw) ? [kw, ...a.keywords] : a.keywords,
  }));
}
function pushAbilities(list: IAbility[]) {
  emit('update:modelValue', normalize(list));
}

// The forced keyword is managed automatically, so hide it from the editable chips.
function displayKeywords(ability: IAbility): string[] {
  return props.forcedKeyword
    ? ability.keywords.filter((k) => k !== props.forcedKeyword)
    : ability.keywords;
}

function addAbility() {
  pushAbilities([...props.modelValue, newAbility()]);
}

function removeAbility(index: number) {
  pushAbilities(props.modelValue.filter((_, i) => i !== index));
}

function isPassive(ability: IAbility): boolean {
  return ability.type === AbilityType.Passive;
}

function updateField(index: number, field: keyof IAbility, value: unknown) {
  const updated = props.modelValue.map((ability, i) => {
    if (i !== index) return ability;
    return { ...ability, [field]: value };
  });
  pushAbilities(updated);
}

// Switching to/from Passive keeps the timing in sync: passive abilities always
// read "Passive"; leaving Passive clears that placeholder so a real timing can
// be entered.
function updateType(index: number, type: number) {
  const updated = props.modelValue.map((ability, i) => {
    if (i !== index) return ability;
    const next = { ...ability, type };
    if (type === AbilityType.Passive) {
      next.timing = 'Passive';
    } else if (ability.type === AbilityType.Passive && ability.timing === 'Passive') {
      next.timing = '';
    }
    return next;
  });
  pushAbilities(updated);
}
</script>

<style scoped>
/* 4-column grid: row 1 = name | timing (span 2 each), row 2 = type, color, icon,
   cost (1 col each), then declare / effect / keywords full-width on their own rows.
   The row card + add/remove chrome lives in EditableList. */
.ability-fields {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--st-space-2);
  align-items: end;
}

.ability-fields :deep(.field--half) {
  grid-column: span 2;
}

.ability-fields :deep(.field--full) {
  grid-column: 1 / -1;
}

/* The id row (when shown) spans the full width above the fields. */
.ability-fields__id {
  grid-column: 1 / -1;
}

/* Forced (lore) keyword shown as a locked chip, mirroring the unit's derived
   keywords. */
.locked-kw {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-1);
  margin-bottom: var(--st-space-1);
}
.locked-kw__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--st-space-1);
  padding: 2px var(--st-space-2);
  background: var(--st-surface-sunken);
  border: 1px dashed var(--st-border-strong);
  border-radius: var(--st-radius-full);
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
}
.locked-kw__lock {
  font-size: var(--st-text-2xs);
  opacity: 0.7;
}
</style>
